import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Loader2, Clock, RefreshCw, Download, Copy } from 'lucide-react';
import { getBillingSummary, getMyQuarterlyInvoices, getMyQuarterlyInvoicePdf, getSubscriptionPaymentInfo } from '../../../redux/api';
import { formatInvoiceEur, formatInvoiceRsd } from '../../../utils/invoiceDisplayFormat';
import {
  formatBillingYmdLocal,
  formatBillingInstantDateTime,
  billingDateLocaleForLanguage,
} from '../../../utils/billingDateFormat';
import { toast } from 'react-toastify';

const InvoicesView = () => {
  const { t, i18n } = useTranslation();
  const dateLoc = billingDateLocaleForLanguage(i18n.language);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [quarterly, setQuarterly] = useState([]);
  const [quarterlyLoading, setQuarterlyLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [paymentInfoReady, setPaymentInfoReady] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: d } = await getBillingSummary();
      setData(d);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Greška');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadQuarterlyPdf = async (inv) => {
    try {
      const { data } = await getMyQuarterlyInvoicePdf(inv._id);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError(t('payments.invoices.pdfError', 'Preuzimanje PDF nije uspelo.'));
    }
  };

  const loadQuarterly = useCallback(async () => {
    setQuarterlyLoading(true);
    try {
      const { data: q } = await getMyQuarterlyInvoices();
      setQuarterly(Array.isArray(q?.invoices) ? q.invoices : []);
    } catch {
      setQuarterly([]);
    } finally {
      setQuarterlyLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadQuarterly();
    getSubscriptionPaymentInfo()
      .then(({ data: info }) => setPaymentInfo(info || null))
      .catch(() => setPaymentInfo(null))
      .finally(() => setPaymentInfoReady(true));
  }, [load, loadQuarterly]);

  const trial = data?.trial;
  const acc = data?.accrual;
  const currentPkg = data?.currentPackage ?? 1;
  const isPaidHeadlinePackage = currentPkg >= 2;
  const now = new Date();
  const trialEnd = trial?.end ? new Date(trial.end) : null;
  const trialCountdown =
    trial?.active && trialEnd
      ? (() => {
          const diff = trialEnd - now;
          if (diff <= 0) return null;
          const days = Math.floor(diff / (86400000));
          const hours = Math.floor((diff % 86400000) / 3600000);
          const minutes = Math.floor((diff % 3600000) / 60000);
          return { days, hours, minutes };
        })()
      : null;

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <div className="flex items-center gap-4">
            <div className="bg-emerald-500/20 rounded-lg p-4">
              <FileText className="h-8 w-8 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">
                {t('payments.invoices.title', 'Fakture')}
              </h1>
              <p className="text-gray-400 mt-1">
                {t('payments.invoices.subtitle', 'Pretplata za pravna lica u Srbiji')}
              </p>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('payments.invoices.loadingBilling', 'Učitavanje obračuna...')}
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-900/30 border border-red-700 px-4 py-3 text-red-200 text-sm flex flex-wrap items-center gap-3">
            {error}
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center gap-1 text-white bg-red-800/50 px-3 py-1 rounded-md hover:bg-red-800"
            >
              <RefreshCw className="h-3 w-3" />
              {t('payments.invoices.retry', 'Pokušaj ponovo')}
            </button>
          </div>
        )}

        {!loading && data?.demo && (
          <p className="text-gray-400 text-sm">
            {t('payments.invoices.demoSkip', 'Za demo nalog obračun se ne prikazuje.')}
          </p>
        )}

        {!loading && data && !data.demo && (
          <>
            <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700 space-y-4">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-400" />
                {t('payments.invoices.trialSection', 'Probni period')}
              </h2>
              {trial?.active && trialCountdown ? (
                <div className="rounded-lg bg-amber-900/20 border border-amber-700/50 px-4 py-3 text-amber-100">
                  <p className="font-medium">
                    {t('payments.invoices.trialActive', 'Besplatni probni period je aktivan.')}
                  </p>
                  <p className="text-2xl font-mono mt-2">
                    {trialCountdown.days}d {String(trialCountdown.hours).padStart(2, '0')}:
                    {String(trialCountdown.minutes).padStart(2, '0')}
                  </p>
                  <p className="text-sm text-amber-200/80 mt-1">
                    {t('payments.invoices.trialUntil', 'Do')}{' '}
                    {trialEnd ? formatBillingInstantDateTime(trialEnd, dateLoc) : '—'}
                  </p>
                  <p className="text-xs text-amber-200/60 mt-2">
                    {t(
                      'payments.invoices.trialNoCharge',
                      'Tokom probnog perioda obračun pretplate (EUR/RSD) je 0.'
                    )}
                  </p>
                </div>
              ) : isPaidHeadlinePackage ? (
                <div className="rounded-lg bg-emerald-900/20 border border-emerald-700/50 px-4 py-3 text-emerald-100">
                  <p className="font-medium">
                    {t(
                      'payments.invoices.trialSupersededByPaidPlan',
                      'Plaćeni paket je aktivan — probni period se na naplatu ne primenjuje.'
                    )}
                  </p>
                  <p className="text-sm text-emerald-200/85 mt-2">
                    {t(
                      'payments.invoices.trialSupersededExplain',
                      'Obračun pretplate ide po izabranom paketu (vidi tekuću potrošnju i tromesečne fakture ispod).'
                    )}
                  </p>
                </div>
              ) : trial?.expiredAwaitingPaidPlan ? (
                <div className="rounded-lg bg-slate-700/50 border border-slate-600 px-4 py-3 text-slate-200">
                  <p className="font-medium text-amber-200/95">
                    {t('payments.invoices.trialExpiredFrozenTitle', 'Probni period je istekao')}
                  </p>
                  <p className="text-sm text-slate-300 mt-2">
                    {t(
                      'payments.invoices.trialExpiredFrozenBody',
                      'Nalog ostaje na Free paketu dok ne izaberete plaćeni plan. Naplata plaćenog paketa ne teče dok ne aktivirate plan na kontrolnoj tabli.'
                    )}
                  </p>
                </div>
              ) : (
                <p className="text-gray-400 text-sm">
                  {t(
                    'payments.invoices.trialInactive',
                    'Probni period nije aktivan ili je istekao. Naplata prati izabrani paket.'
                  )}
                </p>
              )}
            </div>

            <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700 space-y-4">
              <h2 className="text-lg font-semibold text-white">
                {t('payments.invoices.currentPlan', 'Trenutni paket')}:{' '}
                <span className="text-emerald-400">
                  {data.currentPackageLabel} (#{data.currentPackage})
                </span>
              </h2>
              <p className="text-gray-400 text-sm">
                {t(
                  'payments.invoices.changeOnDashboard',
                  'Plan menjate na kontrolnoj tabli dugmetom „Promeni plan”.'
                )}
              </p>
            </div>

            {acc && (
              <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700 space-y-4 text-gray-300">
                <h2 className="text-lg font-semibold text-white">
                  {t('payments.invoices.currentConsumptionTitle', 'Tekuća potrošnja')}
                </h2>
                <p className="text-sm text-gray-400">
                  {t(
                    'payments.invoices.currentConsumptionExplain',
                    'Za svaki dan važi paket iz istorije izbora (planChangeLog): dnevna stavka = cena tog paketa za taj kalendarski mesec / broj dana u mesecu. Posle promene paketa dalje se računa po novom paketu; ukupan iznos je zbir po periodima, ne po trenutnom paketu unazad.'
                  )}
                </p>
                {!acc.periodStartYmd && Number(acc.cumulativeEur) === 0 && (
                  <p className="text-sm text-amber-200/90 bg-amber-900/20 border border-amber-800/40 rounded-lg px-3 py-2">
                    {trial?.expiredAwaitingPaidPlan
                      ? t(
                          'payments.invoices.noAccrualUntilPaidPlan',
                          'Nema obračuna plaćenog paketa dok ne izaberete plaćeni plan na kontrolnoj tabli.'
                        )
                      : t(
                          'payments.invoices.noPaidConsumptionYet',
                          'Još nema obračuna plaćenog paketa (npr. samo Free ili ste još u probnom periodu).'
                        )}
                  </p>
                )}
                {acc.periodStartYmd && (
                  <p className="text-sm text-gray-400">
                    {t('payments.invoices.periodFrom', 'Od')}{' '}
                    <span className="text-gray-200">{formatBillingYmdLocal(acc.periodStartYmd, dateLoc)}</span>
                    {' · '}
                    {t('payments.invoices.periodTo', 'do')}{' '}
                    <span className="text-gray-200">
                      {acc.periodEndYmd ? formatBillingYmdLocal(acc.periodEndYmd, dateLoc) : '—'}
                    </span>
                    {acc.billablePaidDays != null && (
                      <>
                        {' '}
                        ({acc.billablePaidDays}{' '}
                        {t('payments.invoices.billableDays', 'dana naplate')})
                      </>
                    )}
                  </p>
                )}
                {Array.isArray(acc.segments) && acc.segments.length > 0 && (
                  <div className="rounded-lg border border-gray-600 bg-gray-900/40 overflow-hidden">
                    <p className="text-xs font-medium text-gray-400 px-3 py-2 border-b border-gray-600 bg-gray-800/60">
                      {t(
                        'payments.invoices.accrualByPlanTitle',
                        'Obračun po periodima (istorija paketa)'
                      )}
                    </p>
                    <div className="divide-y divide-gray-700/80 text-sm">
                      {acc.segments.map((seg, i) => (
                        <div
                          key={`${seg.fromYmd}-${seg.toYmd}-${seg.package}-${i}`}
                          className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1"
                        >
                          <div>
                            <span className="text-emerald-400 font-medium">
                              {seg.packageLabel} (#{seg.package})
                            </span>
                            <span className="text-gray-500 mx-2">·</span>
                            <span className="text-gray-400 text-xs">
                              {formatBillingYmdLocal(seg.fromYmd, dateLoc)} —{' '}
                              {formatBillingYmdLocal(seg.toYmd, dateLoc)}
                            </span>
                            <span className="text-gray-500 text-xs ml-2">
                              ({seg.days}{' '}
                              {t('payments.invoices.accrualSegmentDaysShort', 'd')})
                            </span>
                          </div>
                          <div className="font-mono text-gray-200">
                            {formatInvoiceEur(seg.eur)} EUR
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg bg-gray-900/50 p-3 border border-gray-700">
                    <dt className="text-gray-500">
                      {t('payments.invoices.cumulativeEur', 'Ukupno do danas (EUR)')}
                    </dt>
                    <dd className="text-xl font-mono text-white">
                      {formatInvoiceEur(acc.cumulativeEur)}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-900/50 p-3 border border-gray-700">
                    <dt className="text-gray-500">
                      {t('payments.invoices.cumulativeRsd', 'Procena u RSD (referentni kurs)')}
                    </dt>
                    <dd className="text-xl font-mono text-white">
                      {formatInvoiceRsd(acc.cumulativeRsdEstimate)}{' '}
                      RSD
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-900/50 p-3 border border-gray-700">
                    <dt className="text-gray-500">EUR → RSD</dt>
                    <dd className="text-white font-mono">
                      {acc.eurToRsdRate != null && Number.isFinite(Number(acc.eurToRsdRate))
                        ? formatInvoiceEur(acc.eurToRsdRate)
                        : '—'}
                    </dd>
                  </div>
                  <div className="rounded-lg bg-gray-900/50 p-3 border border-gray-700">
                    <dt className="text-gray-500">
                      {t('payments.invoices.fullMonthEur', 'Ceo mesec uz trenutni paket (EUR)')}
                    </dt>
                    <dd className="text-white font-mono">
                      {formatInvoiceEur(acc.fullMonthEurIfUnchanged)}
                    </dd>
                  </div>
                </dl>
                <p className="text-xs text-gray-500 leading-relaxed">{acc.eurToRsdNote}</p>
              </div>
            )}
          </>
        )}

        {!loading && data && !data.demo && (
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-400" />
              {t('payments.invoices.quarterlyTitle', 'Tromesečne fakture pretplate')}
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              {t(
                'payments.invoices.quarterlyExplain',
                'Obračun pretplate kreće od 1. maja 2026. na tromesečnom nivou (maj–jul, avg–okt, nov–jan, feb–apr). Faktura ST-… izdaje se posle završetka tromesečja. Plaćanje je na čekanju dok ne potvrdi SpinTasker; zatim stiže zvanična potvrda na mejl.'
              )}
            </p>
            {quarterlyLoading ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('payments.invoices.loadingQuarterly', 'Učitavanje faktura...')}
              </div>
            ) : quarterly.length === 0 ? (
              <p className="text-gray-500 text-sm">
                {t('payments.invoices.quarterlyEmpty', 'Trenutno nemate izdatih tromesečnih faktura.')}
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-700">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-gray-900/80 text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Broj</th>
                      <th className="px-3 py-2">Period</th>
                      <th className="px-3 py-2">Iznos</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Poziv na broj</th>
                      <th className="px-3 py-2">PDF</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-300">
                    {quarterly.map((inv) => (
                      <tr key={inv._id} className="border-t border-gray-700">
                        <td className="px-3 py-2 font-mono text-emerald-300">{inv.invoiceNumber}</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                          {formatBillingYmdLocal(inv.periodStartYmd, dateLoc)} —{' '}
                          {formatBillingYmdLocal(inv.periodEndYmd, dateLoc)}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {formatInvoiceEur(inv.amountEur)} EUR
                          {inv.amountRsdEstimate != null && (
                            <span className="block text-gray-500">
                              ≈ {formatInvoiceRsd(inv.amountRsdEstimate)} RSD
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {inv.status === 'PENDING_PAYMENT' && t('payments.invoices.statusPending', 'Na čekanju uplate')}
                          {inv.status === 'PAID' && t('payments.invoices.statusPaid', 'Plaćeno — potvrda uskoro')}
                          {inv.status === 'OFFICIAL_SENT' &&
                            t('payments.invoices.statusOfficial', 'Zvanična potvrda poslata na mejl')}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-300 break-all max-w-[180px]">
                          {inv.invoiceNumber}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => downloadQuarterlyPdf(inv)}
                            className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300"
                          >
                            <Download className="w-3 h-3" />
                            {t('payments.invoices.downloadPdf', 'Preuzmi')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {paymentInfoReady && paymentInfo && (
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700 space-y-4 text-gray-300">
            <h2 className="text-lg font-semibold text-white">
              {t('payments.invoices.payTitle', 'Podaci za uplatu')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {[
                [t('payments.invoices.payBank', 'Banka'), paymentInfo.recipientBankName],
                [t('payments.invoices.payAccount', 'Broj računa'), paymentInfo.recipientAccountNumber],
                [t('payments.invoices.payHolder', 'Primalac'), paymentInfo.recipientAccountHolder],
                [t('payments.invoices.payPurpose', 'Svrha plaćanja'), paymentInfo.purpose],
              ].map(([label, value]) =>
                value ? (
                  <div key={label} className="rounded-lg bg-gray-900/50 p-3 border border-gray-700">
                    <div className="text-gray-500 mb-1">{label}</div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-white break-all">{value}</span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(String(value));
                          toast.success(t('payments.invoices.copied', 'Kopirano'));
                        }}
                        className="shrink-0 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-700"
                        aria-label={t('payments.invoices.copy', 'Kopiraj')}
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : null
              )}
            </div>
            <p className="text-sm text-gray-400 leading-relaxed">{t('payments.invoices.p1')}</p>
            <p className="text-xs text-gray-500">{t('payments.invoices.disclaimer')}</p>
          </div>
        )}
        {paymentInfoReady && !paymentInfo && (
          <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700 space-y-4 text-gray-300 leading-relaxed">
            <p>{t('payments.invoices.p1')}</p>
            <p className="text-sm text-gray-500">{t('payments.invoices.disclaimer')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesView;
